import { addToast, dismissToast, generateId, updateToast } from './toast-store';
import type { PromiseToastOptions, ToastOptions, ToastVariant } from './types';

const DEFAULT_DURATION = 4000;
const ACTION_DURATION = 8000;

function createToast(
  title: string,
  variant: ToastVariant,
  options?: ToastOptions,
): string {
  const id = options?.id ?? generateId();
  const duration =
    options?.duration ??
    (options?.action ? ACTION_DURATION : DEFAULT_DURATION);

  addToast({
    id,
    title,
    variant,
    description: options?.description,
    duration,
    action: options?.action,
    dismissible: options?.dismissible ?? true,
    onDismiss: options?.onDismiss,
    onAutoClose: options?.onAutoClose,
    createdAt: Date.now(),
  });

  return id;
}

function toast(title: string, options?: ToastOptions): string {
  return createToast(title, options?.variant ?? 'default', options);
}

toast.success = (title: string, options?: ToastOptions): string =>
  createToast(title, 'success', options);

toast.error = (title: string, options?: ToastOptions): string =>
  createToast(title, 'error', options);

toast.warning = (title: string, options?: ToastOptions): string =>
  createToast(title, 'warning', options);

toast.info = (title: string, options?: ToastOptions): string =>
  createToast(title, 'info', options);

toast.loading = (title: string, options?: ToastOptions): string =>
  createToast(title, 'default', { ...options, duration: Infinity });

toast.promise = <T>(
  promise: Promise<T>,
  options: PromiseToastOptions<T>,
): Promise<T> => {
  const id = toast.loading(options.loading, {
    description: options.description,
  });

  promise.then(
    (data) => {
      const message =
        typeof options.success === 'function'
          ? options.success(data)
          : options.success;
      updateToast(id, {
        title: message,
        variant: 'success',
        duration: DEFAULT_DURATION,
        createdAt: Date.now(),
      });
    },
    (err) => {
      const message =
        typeof options.error === 'function'
          ? options.error(err)
          : options.error;
      updateToast(id, {
        title: message,
        variant: 'error',
        duration: DEFAULT_DURATION,
        createdAt: Date.now(),
      });
    },
  );

  return promise;
};

toast.dismiss = (id?: string): void => {
  dismissToast(id);
};

export { toast };
